import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ServerResponse } from '../models/ServerResponse';
import { NotificationList } from '../models/NotificationList';
import { Observable, Subject, of, timer } from 'rxjs';
import { finalize, map, repeat, repeatWhen, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { Pagination } from '../models/Pagination';
import { Notification } from '../models/Notification';
import { Where } from '../helpers/QueryOptions';
import { MatSnackBarService } from './mat-snack-bar.service';
import { eNotificationType } from '../models/eNotificationType';
import { RouterService } from './router-service.service';
import { eMySalesPageTabString } from '../modules/my-sales/classes/eMySalesPageTab';
import { eMyPurchasesPageTabString } from '../modules/my-purchases/classes/eMyPurchasesPageTab';
import { AsyncPaginatorData } from '../modules/shared/classes/AsyncPaginatorData';
import { AuthService } from '../modules/auth/services/auth.service';

@Injectable({
	providedIn: 'root'
})
export class NotificationsService {

	readonly API_NOTIFICATIONS_URL : string;
	last? : number;
	pollingInterval = 30000;

	private sMoreNotifications = new Subject<Notification[]>();
	oMoreNotifications = this.sMoreNotifications.asObservable();
	shutPolling$ = new Subject<void>();
	restartPolling$ = new Subject<void>();

	constructor(private http:HttpClient,
				private matSnackBarService:MatSnackBarService,
				private routerService:RouterService,
				private auth:AuthService) {

		this.API_NOTIFICATIONS_URL = environment.API_URL + "/notifications";

		let bLoopWorking = true;
		timer(this.pollingInterval, this.pollingInterval).pipe(
			switchMap(() => this.getNew()),
			takeUntil(this.shutPolling$),
			finalize(()=> bLoopWorking = false),
			repeatWhen(()=> this.restartPolling$),
		).subscribe({
			next: (n:Notification[]|null) => {
				if (n == null) {
					this.shutPolling$.next();
					this.matSnackBarService.show("error", "Ocurrió un error al cargar tus notificaciones");
				} else {
					this.sMoreNotifications.next(n);
				}
			},
			error: (error: any) => {
				// Si cae acá termina el polling
			},
		});

		this.auth.oUser.subscribe(u => {
			if (u) {
				if (!bLoopWorking) {
					bLoopWorking = true
					this.restartPolling$.next();
				}
			} else {
				this.shutPolling$.next();
			}
		});
	}

	latest() : Observable<NotificationList> {
		let route = this.API_NOTIFICATIONS_URL + "/latest";
		return this.http.get<ServerResponse<NotificationList>>(route).pipe(
			map((sr:ServerResponse<NotificationList>)=> {
				this.defineLastRead(sr.response!.notifications);
				return sr.response!;
			})
		);
	}
	list(apd:AsyncPaginatorData, types?:eNotificationType[]) : Observable<Pagination<Notification>> {
		let route = this.API_NOTIFICATIONS_URL + "/list";
		let qo = apd.toQueryOptions();

		if (types) {
			qo.wheres = [new Where("in", "type", types)];
		}

		return this.http.post<ServerResponse<Pagination<Notification>>>(route, qo).pipe(
			map((sr:ServerResponse<Pagination<Notification>>)=> {	
				return sr.response!;
			})
		);
	}
	markAsRead(notification_id:number|number[]|"all") : Observable<true> {
		let route = this.API_NOTIFICATIONS_URL + "/read";
		return this.http.post<ServerResponse<any>>(route, {id: notification_id}).pipe(
			map((sr:ServerResponse<any>)=> {
				return sr.response!;
			})
		);
	}
	getNew() : Observable<Notification[]|null> {
		let route = this.API_NOTIFICATIONS_URL + "/get-new";
		return this.http.post<ServerResponse<Notification[]>>(route, {last_id: this.last}).pipe(
			map((sr:ServerResponse<Notification[]>)=> {
				this.defineLastRead(sr.response!);
				return sr.response!;
			})
		);
	}

	private defineLastRead(notifications:Notification[]) {
		if (notifications.length) {
			this.last = notifications[0].id;
		}
	}

	//#region Sensibles a cambios en tipos de notificaciones
	notificationAction(n:Notification) {
		switch(n.type) {
			case eNotificationType.SELLER_REGISTER_COMPLETE : {
				this.auth.me().subscribe();		// Para que actualice el token
				this.routerService.toMySales(eMySalesPageTabString.SALES);
				return;
			}
			case eNotificationType.SELLER_REJECTED : {
				this.routerService.toHome();
				return;
			}
			case eNotificationType.ACCOUNT_IN_REVIEW : {
				this.routerService.toHome();
				return;
			}
			case eNotificationType.ABANDONED_CART : {
				this.routerService.toCartsList();
				return;
			}
			case eNotificationType.PURCHASED_PRODUCT_SELLER : {
				this.routerService.toMySales(eMySalesPageTabString.SALES);
				return;
			}
			case eNotificationType.PURCHASED_PRODUCT_BUYER : {
				this.routerService.toMyPurchases(eMyPurchasesPageTabString.PURCHASES);
				return;
			}
			case eNotificationType.ADDED_QUESTION : {
				this.routerService.toMySales(eMySalesPageTabString.QUESTIONS);
				return;
			}
			case eNotificationType.QUESTION_ANSWERED : {
				let metadata = JSON.parse(n.metadata);
				this.routerService.toProduct(metadata.product_id);
				return;
			}
			case eNotificationType.SHIPMENT_PICKED_UP : {
				this.routerService.toMySales(eMySalesPageTabString.SALES);
				return;
			}
			case eNotificationType.SHIPMENT_IN_PROCESS : {
				this.routerService.toMyPurchases(eMyPurchasesPageTabString.PURCHASES);
				return;
			}
			case eNotificationType.SHIPMENT_DELIVERED : {
				this.routerService.toMySales(eMySalesPageTabString.SALES);
				return;
			}
			case eNotificationType.PURCHASE_RECEIVED : {
				this.routerService.toMyPurchases(eMyPurchasesPageTabString.PURCHASES);
				return;
			}
			case eNotificationType.NEW_ECOTREND : {
				let metadata = JSON.parse(n.metadata);
				this.routerService.toEcotrends(metadata.slug);
				return;
			}
			case eNotificationType.SELLER_UPLOADED_INVOICE : {
				this.routerService.toMyPurchases(eMyPurchasesPageTabString.PURCHASES);
				return;
			}
			// Si no hay una acción acá para un tipo específico, no hay nada para hacer y no se rompe nada
		}
	}
	getNotificationTypesForFiltering(userIsStore:boolean) : {caption:string, types:eNotificationType[]}[] {
		if (userIsStore) {
			return [
				{
					caption: "Ventas",
					types: [
						eNotificationType.PURCHASED_PRODUCT_SELLER,
						eNotificationType.SHIPMENT_PICKED_UP,
						eNotificationType.SHIPMENT_DELIVERED,
					]
				},
				{
					caption: "Compras",
					types: [
						eNotificationType.PURCHASED_PRODUCT_BUYER,
						eNotificationType.SHIPMENT_IN_PROCESS,
						eNotificationType.PURCHASE_RECEIVED,
						eNotificationType.CUSTOMER_NOT_PRESENT,
						eNotificationType.SELLER_UPLOADED_INVOICE
					]
				},
				{
					caption: "Preguntas en mis publicaciones",
					types: [
						eNotificationType.ADDED_QUESTION,
					]
				},
				{
					caption: "Mis preguntas",
					types: [
						eNotificationType.QUESTION_ANSWERED
					]
				},
				{
					caption: "Mi cuenta",
					types: [
						eNotificationType.SELLER_REGISTER_COMPLETE,
						eNotificationType.SELLER_REJECTED,
						eNotificationType.ACCOUNT_IN_REVIEW
					]
				},
				{
					caption: "Otras",
					types: [
						eNotificationType.ABANDONED_CART,
						eNotificationType.NEW_ECOTREND,
						eNotificationType.EMITTED_MANUALLY,
					]
				},
			]
		} else {
			return [
				{
					caption: "Compras",
					types: [
						eNotificationType.PURCHASED_PRODUCT_BUYER,
						eNotificationType.SHIPMENT_IN_PROCESS,
						eNotificationType.PURCHASE_RECEIVED,
						eNotificationType.CUSTOMER_NOT_PRESENT,
						eNotificationType.SELLER_UPLOADED_INVOICE
					]
				},
				{
					caption: "Mis preguntas",
					types: [
						eNotificationType.QUESTION_ANSWERED
					]
				},
				{
					caption: "Otras",
					types: [
						eNotificationType.ABANDONED_CART,
						eNotificationType.NEW_ECOTREND,
						eNotificationType.EMITTED_MANUALLY,
					]
				},
			]
		}
	}
	//#endregion
}